import { useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 400;

/**
 * Multi-leg silniční trasa pro PripravaDavky overview mapu.
 *
 * Volá `GET /api/rozvoz/davky/{davkaId}/full-route` po každé změně pořadí zastávek
 * (debounced 400 ms). Backend čte aktuální `rozvoz_poradi` z DB, takže parametr
 * `orderSignature` slouží jen jako trigger pro refetch po POST /preradit response.
 *
 * Vrací plochou geometrii (slepené legy) pro MapaCanvas `routeGeometry` prop.
 *
 * @return {{ geometry: [number,number][], totalDurationSec: number|null, loading: boolean }}
 */
export default function useFullRoute({ davkaId, orderSignature, enabled = true }) {
    const [geometry, setGeometry] = useState([]);
    const [totalDurationSec, setTotalDurationSec] = useState(null);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => {
        if (!enabled || !davkaId) {
            setGeometry([]); setTotalDurationSec(null);
            return;
        }

        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            abortRef.current?.abort?.();
            const ac = new AbortController();
            abortRef.current = ac;

            setLoading(true);
            try {
                const r = await fetch(`/api/rozvoz/davky/${davkaId}/full-route`, {
                    credentials: 'same-origin', signal: ac.signal,
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const json = await r.json();
                const flat = [];
                (json.legs || []).forEach(leg => {
                    (leg.geometry || []).forEach(p => flat.push(p));
                });
                setGeometry(flat);
                setTotalDurationSec(json.total_duration_sec ?? null);
            } catch (e) {
                if (e?.name !== 'AbortError') {
                    setGeometry([]); setTotalDurationSec(null);
                }
            } finally {
                setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timerRef.current);
    }, [davkaId, orderSignature, enabled]);

    return { geometry, totalDurationSec, loading };
}
